import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { useLocation } from 'react-router-dom';

import { v4 as uuidv4 } from 'uuid';

import { styled } from '@mui/material/styles';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import { StepIconProps } from '@mui/material/StepIcon';
import { Button, StepLabel } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import Modal from 'src/presentations/components/molecules/modal';
import { openToast } from 'src/use-cases/toast/useToast';

import { useGetDomainMetaData, useUploadInvoice } from 'src/use-cases/invoice/useInvoices';
import { useUploadData } from 'src/use-cases/upload/useUploadData';
import { validateFileExtension } from 'src/utils/fileValidation';
import { InvoiceUploadSignedUrlRequestDto } from 'src/generated/services/TFinancialApi';
import IconExport from 'src/assets/images/icons/upload-icon.svg';
import IconUpload from 'src/assets/images/icons/document-uploaded.svg';
import Snackbar from 'src/presentations/components/atom/snackbar/Snackbar';
import { UploadStep } from './types';
import { TopInfo } from './TopInfo';
import { MainContent } from './MainContent';
import { useUploadUI } from './useUploadUI';

const steps = [
  { id: UploadStep.Step1_SelectInvoice, label: 'Select Invoices' },
  { id: UploadStep.Step2_UploadStatus, label: 'Processing Status' },
];

const StyledModal = styled(Modal)({
  '& .MuiDialog-paper': {
    maxWidth: '1000px',
    height: 'calc(100% - 64px)',
    borderRadius: '16px',

    '& .MuiDialogContent-root': {
      padding: '0',
      backgroundColor: '#F5F6FA',
    },
  },
});

export const StyledStepper = styled(Stepper)({
  '& .MuiStepConnector-root': {
    padding: '0 8px',
  },
  '& .MuiStepConnector-line': {
    borderTopStyle: 'dashed',
    borderTopWidth: '2px',
  },
  '& .Mui-active .MuiStepConnector-line, & .Mui-completed .MuiStepConnector-line': {
    borderTopStyle: 'solid',
    borderColor: '#00992B',
  },
  '& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed': {
    color: '#00992B',
    fontWeight: 'bold',
  },
  '& .MuiStepIcon-root': {
    color: '#ffffff',
    width: '32px',
    height: '32px',
    border: '1px solid #DFDFDF',
    borderRadius: '100%',

    '& .MuiStepIcon-text': {
      fill: '#00992B',
      fontWeight: 'bold',
    },

    '&.Mui-active': {
      color: '#00992B',
      borderColor: '#00992B',

      '& .MuiStepIcon-text': {
        fill: '#ffffff',
      },
    },
  },
});

export const UploadInvoice = () => {
  const {
    invoiceFiles,
    isShowUpload,
    isMinimized,
    activeStep,
    modality,
    batchId,
    serviceProvider,
    uploadResults,
    isLoadingUpload,
    setIsMinimized,
    setActiveStep,
    setUploadResults,
    setBatchId,
    setIsLoadingUpload,
    onCloseUpload,
    onResetUpload,
    setErrorProcessingStatuses,
  } = useUploadUI();

  // Processing Data Management
  const { isProcessingComplete, resetProcessingData } = useUploadData(batchId, true);

  const isMinimizedRef = useRef<boolean>();
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  isMinimizedRef.current = isMinimized;

  const { data: domainMetaData } = useGetDomainMetaData();
  const { mutateAsync: runUploadInvoice } = useUploadInvoice();

  useEffect(() => {
    if (domainMetaData) {
      setErrorProcessingStatuses(domainMetaData?.errorProcessingStatuses ?? []);
    }
  }, [domainMetaData, setErrorProcessingStatuses]);

  useEffect(() => {
    if (isShowUpload) {
      setIsMinimized(true);
      document.body.style.overflow = 'visible';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (isShowUpload && isMinimized && isProcessingComplete) {
      autoCloseTimerRef.current = setTimeout(() => {
        document.body.style.overflow = 'visible';
        onCloseUpload();
        setTimeout(() => {
          resetProcessingData(); // Reset processing data
          onResetUpload();
        }, 500);
      }, 7000);
    }
    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowUpload, isMinimized, isProcessingComplete]);

  const validateFiles = (files: File[], allowedExtensions: string[]): string[] => {
    const invalidFiles: string[] = [];
    for (const file of files) {
      const { isValid } = validateFileExtension(file.name, allowedExtensions);
      if (!isValid) {
        invalidFiles.push(file.name);
      }
    }
    return invalidFiles;
  };

  const onUploadInvoices = async () => {
    setIsLoadingUpload(true);

    const files = invoiceFiles;

    if (files?.length > 0) {
      // Validate file extensions if we have the list of allowed extensions
      if (domainMetaData?.allowedFileExtensions && domainMetaData.allowedFileExtensions.length > 0) {
        const invalidFiles = validateFiles(files, domainMetaData.allowedFileExtensions);

        // If any files have invalid extensions, show error alert
        if (invalidFiles.length > 0) {
          setIsLoadingUpload(false);
          // Show error using the application's toast system
          openToast({
            title: 'Invalid File Extension',
            description: `Files must have exact extensions: ${domainMetaData.allowedFileExtensions.join(', ')}`,
            toastType: 'error',
            toastOptions: {
              autoClose: 7000,
            },
          });

          // Still log the error to console for debugging
          console.error('File extension validation error:', {
            invalidFiles,
            allowedExtensions: domainMetaData.allowedFileExtensions,
          });
          return;
        }
      }

      const fileNames = files.map((file: File) => file?.name);

      const uploadData = {
        serviceProviderCode: serviceProvider ?? null,
        modality: modality ?? null,
        fileNames,
        batchId: uuidv4(),
      } as InvoiceUploadSignedUrlRequestDto;

      try {
        const uploadResp = await runUploadInvoice(uploadData);
        setIsLoadingUpload(false);
        setBatchId(uploadResp?.batchId || '');
        setUploadResults(uploadResp?.uploadUrls || []);
        setActiveStep(UploadStep.Step2_UploadStatus);
      } catch (error) {
        console.warn(error);
        setIsLoadingUpload(false);
      }
    }
  };

  const CustomStepIcon = (props: StepIconProps) => {
    const { active, completed, icon } = props;

    return (
      <span
        className={clsx('flex items-center justify-center w-36 h-36 text-20 font-bold rounded-full', {
          'bg-primary/25 text-primary': completed,
          'bg-primary text-white': active,
          'bg-white text-primary border border-neutral-20': !active && !completed,
        })}
      >
        {icon}
      </span>
    );
  };

  return (
    <>
      <StyledModal open={isShowUpload} onClose={onCloseUpload} fullWidth maxWidth='md' sx={{ visibility: isMinimized ? 'hidden' : 'visible' }}>
        <div className='h-full flex flex-col'>
          <div className='px-24 pb-20 pt-24 border-b border-neutral-20 bg-white'>
            <div className='py-12 px-24 bg-neutral-10 rounded-8 mb-20'>
              <StyledStepper activeStep={activeStep}>
                {steps.map(({ id, label }) => (
                  <Step key={id}>
                    <StepLabel color='inherit' StepIconComponent={CustomStepIcon}>
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </StyledStepper>
            </div>

            <TopInfo />
          </div>

          <MainContent />

          <div className='mt-auto px-24 py-20 border-t border-neutral-20 flex items-center justify-end gap-12 bg-white'>
            {activeStep === UploadStep.Step1_SelectInvoice && (
              <>
                <Button type='button' variant='outlined' color='inherit' onClick={onCloseUpload} sx={{ textTransform: 'capitalize', borderColor: '#dfdfdf' }}>
                  Discard
                </Button>
                <Button
                  type='button'
                  variant='contained'
                  color='primary'
                  onClick={onUploadInvoices}
                  sx={{ textTransform: 'capitalize' }}
                  disabled={!invoiceFiles || invoiceFiles?.length === 0}
                >
                  {isLoadingUpload ? 'Loading...' : 'Start Upload'}
                </Button>
              </>
            )}
            {activeStep === UploadStep.Step2_UploadStatus && (
              <Button
                type='button'
                variant='outlined'
                color='inherit'
                disabled={!batchId || batchId === ''}
                onClick={() => {
                  if (batchId && batchId !== '') {
                    setIsMinimized(true);
                    document.body.style.overflow = 'visible';
                  }
                }}
                sx={{ textTransform: 'capitalize', borderColor: '#dfdfdf' }}
              >
                Close
              </Button>
            )}
          </div>
        </div>
      </StyledModal>

      {isShowUpload && isMinimized && !isProcessingComplete && (
        <Snackbar
          autoSidebarPlacement
          icon={<img src={IconExport} alt='' />}
          content={
            <div className='pr-48 flex items-center'>
              {uploadResults?.length > 1 ? `${uploadResults?.length} Invoices ` : `${uploadResults?.length} Invoice `} Invoices being processed.
            </div>
          }
          actions={
            <button
              type='button'
              className='pr-20 flex items-center gap-10 text-green-70 font-semibold'
              onClick={() => {
                setIsMinimized(false);
                document.body.style.overflow = 'hidden';
              }}
            >
              Show Details
            </button>
          }
        />
      )}

      {isShowUpload && isMinimized && isProcessingComplete && (
        <Snackbar
          zIndex={9999}
          autoSidebarPlacement
          icon={<img src={IconUpload} alt='document upload icon' />}
          content={
            <div className='flex items-center pl-5 pr-40'>
              Processing of {uploadResults?.length > 1 ? `${uploadResults?.length} Invoices ` : `${uploadResults?.length} Invoice `} Invoices completed.
            </div>
          }
          actions={
            <>
              <button
                type='button'
                className='pr-20 flex items-center gap-10 text-green-70 font-semibold'
                onClick={() => {
                  setIsMinimized(false);
                  document.body.style.overflow = 'hidden';
                }}
              >
                Show Details
              </button>
              <button
                type='button'
                aria-label='dialog box'
                className='font-medium text-primary text-14'
                onClick={() => {
                  document.body.style.overflow = 'visible';
                  onCloseUpload();
                  setTimeout(() => {
                    resetProcessingData(); // Reset processing data
                    onResetUpload();
                  }, 500);
                }}
              >
                <CloseIcon sx={{ color: '#808080' }} />
              </button>
            </>
          }
        />
      )}
    </>
  );
};
